import { EmpreenderApps } from '../../lib/AppsArray';
import { type ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal from '../Modal/Modal';
import ModalContent from '../Modal/ModalContent';
import AppModal from '../AppModal';
import { useUser } from '../../UserContext';
import { useSubMenu } from '../../SubMenuContext';
import { eplusHub } from '../../api/eplus';
import { ChevronUp, Plus, Search, Star, X } from 'lucide-react';
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '../Drawer';
import { useTranslation } from '../../lib/i18n/TranslationContext';
import SkelletonLoader from '../Skelleton';
import AppPreview from '../AppPreview';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../Tooltip/Tooltip';
import Input from '../Input';
import { ALPHABET_INDEX, buildBarApps, filterDrawerApps, getAvailableLetters, getEffectivePinnedKeys, getSectionScrollTop, groupAppsByLetter, normalizeAppKey, normalizeEplus, QuickAccessAppProps, resolveActiveLetterFromScroll, trackClick } from './QuickAcessHelper';

const PLUS_LOGO_GIF_URL = 'https://empreender.nyc3.digitaloceanspaces.com/landingpage/6a148d32ea8c0.gif';

const NavigationAppsQuickAcessMenu = () => {
    const { user } = useUser();
    const { appEndPoint, headers, app: currentAppKey } = useSubMenu();
    const { t } = useTranslation();
    const [modalAppOpen, setModalAppOpen] = useState(false);
    const [appActive, setAppActive] = useState<QuickAccessAppProps | undefined>(undefined);
    const [appsDrawerOpen, setAppsDrawerOpen] = useState(false);
    const [isSavingPinned, setIsSavingPinned] = useState(false);
    const [fixedBarOverride, setFixedBarOverride] = useState<string[] | null>(null);
    const [listFilter, setListFilter] = useState<'all' | 'favorites'>('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [activeLetter, setActiveLetter] = useState<string | null>(null);

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const sectionHeaderRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const lastHoveredBarIndexRef = useRef<number | null>(null);
    const [previewMotion, setPreviewMotion] = useState<'from-start' | 'from-end'>('from-end');

    const plusApp = useMemo(() => EmpreenderApps.find((app) => app.slug === 'plus'), []);

    const pinnedList = fixedBarOverride !== null ? fixedBarOverride : user?.fixed_apps_fast_login_bar;
    const isUserLoading = Boolean(user?.loading);
    const selectedAppEplus = useMemo(() => {
        if (!currentAppKey) return undefined;

        const normalized = normalizeAppKey(String(currentAppKey));
        const current = EmpreenderApps.find((a) => {
            const bySlug = normalizeAppKey(a.slug) === normalized;
            const byTag = Array.isArray(a.tags) ? a.tags.some((tag) => normalizeAppKey(String(tag)) === normalized) : false;
            return bySlug || byTag;
        });

        return current?.eplus;
    }, [currentAppKey]);

    const isAppSelected = (app: QuickAccessAppProps) => (selectedAppEplus ? normalizeEplus(selectedAppEplus) === normalizeEplus(app.eplus) : false);

    const handleBarAppPointerEnter = useCallback((index: number) => {
        const prev = lastHoveredBarIndexRef.current;
        if (prev !== null && prev !== index) {
            setPreviewMotion(index > prev ? 'from-end' : 'from-start');
        }
        lastHoveredBarIndexRef.current = index;
    }, []);

    const handleOpenModal = (app: QuickAccessAppProps) => {
        setModalAppOpen(!modalAppOpen);

        setAppActive(app);
    };

    const visibleApps = useMemo(() => EmpreenderApps.filter((app) => !app.hide) as QuickAccessAppProps[], []);

    const pinnedKeys = useMemo(() => getEffectivePinnedKeys(pinnedList, visibleApps), [pinnedList, visibleApps]);

    const isPinnedOnBar = useCallback((app: QuickAccessAppProps) => pinnedKeys.includes(normalizeEplus(app.eplus)), [pinnedKeys]);

    const pinnedCount = useMemo(() => visibleApps.filter((app) => isPinnedOnBar(app)).length, [visibleApps, isPinnedOnBar]);

    const barApps = useMemo(() => buildBarApps(visibleApps, pinnedList), [visibleApps, pinnedList]);

    const filteredApps = useMemo(() => filterDrawerApps(visibleApps, listFilter, searchQuery, isPinnedOnBar), [visibleApps, listFilter, searchQuery, isPinnedOnBar]);

    const drawerApps = useMemo(() => [...filteredApps].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')), [filteredApps]);

    const availableLetters = useMemo(() => getAvailableLetters(filteredApps), [filteredApps]);

    const groupedByLetter = useMemo(() => groupAppsByLetter(drawerApps), [drawerApps]);

    const savePinnedKeys = async (nextUnique: string[], overrideBefore: string[] | null) => {
        setFixedBarOverride(nextUnique);

        if (!appEndPoint) {
            return;
        }

        setIsSavingPinned(true);

        const res = await eplusHub(
            {
                method: 'POST',
                credentials: 'include' as RequestCredentials,
                headers,
                body: JSON.stringify({
                    acao: 'salvarAppsFixadosNaBarraDeAcessoRapido',
                    fixed_apps: nextUnique,
                }),
            },
            appEndPoint,
        );

        setIsSavingPinned(false);

        if (res?.code !== 200) {
            setFixedBarOverride(overrideBefore);
        }
    };

    const handleTogglePinnedApp = async (app: QuickAccessAppProps) => {
        if (isSavingPinned) return;

        const keyNorm = normalizeEplus(app.eplus);
        const isOn = pinnedKeys.includes(keyNorm);
        const nextUnique = [...new Set(isOn ? pinnedKeys.filter((k) => k !== keyNorm) : [...pinnedKeys, keyNorm])];

        await savePinnedKeys(nextUnique, fixedBarOverride);
    };

    const syncActiveLetterFromScroll = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const current = resolveActiveLetterFromScroll(container, groupedByLetter, sectionRefs.current, sectionHeaderRefs.current);
        setActiveLetter((prev) => (prev === current ? prev : current));
    }, [groupedByLetter]);

    const scrollToLetter = (letter: string) => {
        const container = scrollContainerRef.current;
        const targetEl = sectionHeaderRefs.current[letter] ?? sectionRefs.current[letter];
        if (!availableLetters.has(letter) || !container || !targetEl) return;

        setActiveLetter(letter);
        container.scrollTo({
            top: Math.max(0, getSectionScrollTop(targetEl, container)),
            behavior: 'smooth',
        });
    };

    const scrollToTop = () => {
        setActiveLetter(null);
        scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    };

    useEffect(() => {
        if (!appsDrawerOpen || groupedByLetter.length === 0) {
            return;
        }

        let rafId = 0;
        let container: HTMLDivElement | null = null;
        let resizeObserver: ResizeObserver | null = null;

        const attach = () => {
            container = scrollContainerRef.current;
            if (!container) {
                rafId = requestAnimationFrame(attach);
                return;
            }

            syncActiveLetterFromScroll();
            container.addEventListener('scroll', syncActiveLetterFromScroll, { passive: true });
            resizeObserver = new ResizeObserver(syncActiveLetterFromScroll);
            resizeObserver.observe(container);
        };

        rafId = requestAnimationFrame(attach);

        return () => {
            cancelAnimationFrame(rafId);
            if (container) {
                container.removeEventListener('scroll', syncActiveLetterFromScroll);
            }
            resizeObserver?.disconnect();
        };
    }, [appsDrawerOpen, groupedByLetter, syncActiveLetterFromScroll]);

    const handleAppClick = async (app: QuickAccessAppProps) => {
        if (Object.keys(user?.apps || {}).length <= 0) {
            trackClick(app.eplus);
            window.open(`https://app.empreender.plus/?app=${app.eplus}`, '_blank');
            return;
        }

        if (appEndPoint) {
            const res = await eplusHub(
                {
                    method: 'POST',
                    credentials: 'include' as RequestCredentials,
                    headers,
                    body: JSON.stringify({
                        acao: 'buscarContasDoApp',
                        nome_key: app.eplus,
                        generate_login_link_if_has_only_one_account: true,
                        origin: 'QUICK_ACCESS_MENU',
                    }),
                },
                appEndPoint,
            );

            if (res?.code === 200) {
                const contas = res.data?.contas ?? [];

                if (contas.length === 1 && contas[0]?.link_magico_redirecionar) {
                    window.location.assign(contas[0].link_magico_redirecionar);
                    return;
                }
            }
        }

        handleOpenModal(app);
    };

    return (
        <aside className='empreender-lib-quick-access-menu h-full w-[45px] min-w-[45px] bg-[#f3f4f6] border-r border-white/10 flex flex-col items-center !py-4 max-[759px]:hidden'>
            <a href='https://app.empreender.plus' target='_blank' className='group mb-3 flex items-center justify-center size-5 text-gray-900 font-bold text-base no-underline' aria-label={plusApp?.name ?? 'Plus'} title={plusApp?.name}>
                <span className='relative block size-5 shrink-0'>
                    <span className='flex size-5 items-center justify-center transition-opacity duration-150 ease-out group-hover:opacity-0 group-focus-visible:opacity-0 [&_svg]:size-5 [&_svg]:shrink-0'>{plusApp?.icon}</span>
                    <img src={PLUS_LOGO_GIF_URL} alt='' width={20} height={20} decoding='async' className='pointer-events-none absolute inset-0 size-5 opacity-0 transition-opacity duration-150 ease-out group-hover:opacity-100 group-focus-visible:opacity-100 scale-[1.2]' aria-hidden />
                </span>
            </a>

            <TooltipProvider delayDuration={0} skipDelayDuration={300} disableHoverableContent>
                <div className='flex-1 w-full overflow-y-auto px-2 custom-scroll [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:w-0 [&::-webkit-scrollbar]:h-0'>
                    <div className='flex flex-col gap-2 items-center pb-2'>
                        {isUserLoading
                            ? Array.from({ length: 5 }, (_, i) => (
                                  <div key={`quick-skel-${i}`} className='size-8 rounded-xl overflow-hidden bg-gray-200 shrink-0 flex items-center justify-center' aria-hidden>
                                      <SkelletonLoader className='!h-8 !w-8 rounded-xl !max-w-none bg-white/20' width='34px' />
                                  </div>
                              ))
                            : barApps.map((app, index) => {
                                  const isSelected = isAppSelected(app);
                                  return (
                                      <div key={app.eplus} className='group relative flex w-full shrink-0 justify-center'>
                                          {isSelected ? (
                                              <span className='pointer-events-none absolute -left-2 top-1/2 z-[1] h-[30px] w-1 -translate-y-1/2 rounded-r-full bg-gray-900' aria-hidden />
                                          ) : (
                                              <span
                                                  className='pointer-events-none absolute -left-2 top-1/2 z-[1] h-[20px] w-1 -translate-y-1/2 origin-center scale-x-0 scale-y-0 rounded-r-full bg-gray-900 opacity-0 transition-[transform,opacity] duration-[320ms] ease-[cubic-bezier(0.34,1.56,0.64,1)] motion-reduce:transition-none group-hover:scale-x-100 group-hover:scale-y-100 group-hover:opacity-100 group-focus-within:scale-x-100 group-focus-within:scale-y-100 group-focus-within:opacity-100'
                                                  aria-hidden
                                              />
                                          )}
                                          <Tooltip>
                                              <TooltipTrigger asChild>
                                                  <button
                                                      type='button'
                                                      onPointerEnter={() => handleBarAppPointerEnter(index)}
                                                      onClick={() => handleAppClick(app)}
                                                      className={`size-[34px] rounded-lg transition-all flex items-center justify-center text-gray-900`}
                                                      aria-label={app.name}
                                                      aria-current={isSelected ? 'page' : undefined}
                                                  >
                                                      <span className='[&_svg]:size-5 [&_svg]:shrink-0'>{app.icon}</span>
                                                  </button>
                                              </TooltipTrigger>
                                              <TooltipContent
                                                  side='right'
                                                  align='center'
                                                  sideOffset={10}
                                                  hideArrow
                                                  className='z-[120] max-w-none overflow-visible border-0 bg-transparent p-0 text-inherit shadow-none animate-none data-[state=open]:animate-none data-[state=closed]:animate-none data-[side=right]:animate-none'
                                              >
                                                  <div className='group/animation' data-motion={previewMotion}>
                                                      <AppPreview app={app} eplusApps={user?.apps ?? {}} hideActions />
                                                  </div>
                                              </TooltipContent>
                                          </Tooltip>
                                      </div>
                                  );
                              })}
                    </div>
                    <div className='sticky bottom-0 pt-3 pb-2 flex justify-center bg-gradient-to-t from-[#f3f4f6] via-[#f3f4f6] to-transparent'>
                        <button type='button' className='size-5 bg-[#f3f4f6] text-white/90 flex items-center justify-center transition-colors' aria-label='Mais apps' onClick={() => setAppsDrawerOpen(true)}>
                            <Plus size={20} className='text-gray-900' />
                        </button>
                    </div>
                </div>
            </TooltipProvider>

            <Drawer open={appsDrawerOpen} onOpenChange={setAppsDrawerOpen} direction='left'>
                <DrawerContent className='!w-[min(420px,100vw)] !max-w-[min(420px,100vw)] !h-full !max-h-[100dvh] bg-white border-r border-gray-200 p-0 flex flex-col overflow-hidden'>
                    <div className='flex flex-1 min-h-0 overflow-hidden'>
                        <div className='flex-1 flex flex-col min-w-0 min-h-0'>
                            <DrawerHeader className='px-4 py-4 border-b border-gray-200 shrink-0'>
                                <div className='flex items-start justify-between gap-2'>
                                    <div className='min-w-0'>
                                        <DrawerTitle className='text-gray-900'>{t('Acesso Rápido')}</DrawerTitle>
                                        <DrawerDescription className='text-gray-600'>
                                            {pinnedCount} de {visibleApps.length} apps fixados
                                        </DrawerDescription>
                                    </div>
                                </div>

                                <div className='mt-3 grid grid-cols-1'>
                                    <Input
                                        type='text'
                                        value={searchQuery}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                            setSearchQuery(e.target.value);
                                            setActiveLetter(null);
                                        }}
                                        placeholder={t('Buscar app...')}
                                        className={`empreender-lib-quick-access-menu-search-input h-10 !py-2 !pl-10 bg-white col-start-1 row-start-1 ${searchQuery ? '!pr-10' : ''}`}
                                        aria-label={t('Buscar app...')}
                                    />
                                    <Search aria-hidden className='pointer-events-none col-start-1 row-start-1 ml-3 size-4 self-center text-gray-400' />
                                    {searchQuery ? (
                                        <button
                                            type='button'
                                            onClick={() => {
                                                setSearchQuery('');
                                                setActiveLetter(null);
                                            }}
                                            className='col-start-1 row-start-1 mr-2 size-6 justify-self-end self-center flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors'
                                            aria-label={t('Limpar busca')}
                                        >
                                            <X className='size-4' aria-hidden />
                                        </button>
                                    ) : null}
                                </div>

                                <div className='flex items-center gap-2 mt-3'>
                                    <button
                                        type='button'
                                        onClick={() => {
                                            setListFilter('all');
                                            setActiveLetter(null);
                                        }}
                                        className={`text-[12px] font-medium px-3 py-1.5 rounded-full transition ${listFilter === 'all' ? 'bg-slate-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                    >
                                        {t('Todos')}
                                    </button>
                                    <button
                                        type='button'
                                        onClick={() => {
                                            setListFilter('favorites');
                                            setActiveLetter(null);
                                        }}
                                        className={`text-[12px] font-medium px-3 py-1.5 rounded-full transition inline-flex items-center gap-1.5 ${listFilter === 'favorites' ? 'bg-slate-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                    >
                                        <Star className={`w-3 h-3 ${listFilter === 'favorites' ? 'text-amber-300 fill-amber-300' : 'text-amber-500 fill-amber-400'}`} aria-hidden />
                                        {t('Favoritos')}
                                        <span className={`ml-0.5 ${listFilter === 'favorites' ? 'text-slate-300' : 'text-slate-400'}`}>{pinnedCount}</span>
                                    </button>
                                </div>
                            </DrawerHeader>

                            <div ref={scrollContainerRef} className='flex-1 min-h-0 overflow-y-auto px-2 py-2 custom-scroll scroll-smooth h-[calc(100dvh-248px)]'>
                                {drawerApps.length === 0 ? (
                                    <p className='px-3 py-8 text-center text-sm text-slate-500'>{listFilter === 'favorites' ? 'Nenhum app nos favoritos.' : 'Nenhum app encontrado.'}</p>
                                ) : (
                                    groupedByLetter.map(([letter, apps]) => (
                                        <div
                                            key={letter}
                                            className='mb-1'
                                            data-section-letter={letter}
                                            ref={(el) => {
                                                if (el) {
                                                    sectionRefs.current[letter] = el;
                                                } else {
                                                    delete sectionRefs.current[letter];
                                                }
                                            }}
                                        >
                                            <div
                                                className='px-3 pt-2 pb-1'
                                                ref={(el) => {
                                                    if (el) {
                                                        sectionHeaderRefs.current[letter] = el;
                                                    } else {
                                                        delete sectionHeaderRefs.current[letter];
                                                    }
                                                }}
                                            >
                                                <span className='text-[11px] font-semibold tracking-[0.12em] text-slate-400'>{letter}</span>
                                            </div>
                                            <ul className='px-1 space-y-1.5'>
                                                {apps.map((app) => {
                                                    const isPinned = isPinnedOnBar(app);
                                                    return (
                                                        <li key={`drawer-${app.slug}-${app.eplus}`}>
                                                            <button
                                                                type='button'
                                                                disabled={isSavingPinned}
                                                                onClick={() => void handleTogglePinnedApp(app)}
                                                                aria-pressed={isPinned}
                                                                aria-label={isPinned ? `Remover ${app.name} dos favoritos` : `Adicionar ${app.name} aos favoritos`}
                                                                className={`group w-full flex items-start gap-3 px-2.5 py-2.5 rounded-lg text-left transition disabled:opacity-60 border ${isPinned ? 'border-[#2EB67D] bg-emerald-200/10 hover:bg-emerald-300/10' : 'border-none  hover:bg-gray-100'}`}
                                                            >
                                                                <span className='mt-0.5 text-gray-900 shrink-0 [&_svg]:size-6 [&_svg]:shrink-0'>{app.icon}</span>
                                                                <div className='min-w-0 flex-1'>
                                                                    <p className='text-[13.5px] font-medium text-slate-900'>{app.name}</p>
                                                                    <p className='text-[11.5px] text-slate-500 leading-snug mt-0.5 line-clamp-2'>{app.content}</p>
                                                                </div>
                                                                <span className={`shrink-0 mt-0.5 w-7 h-7 rounded-full flex items-center justify-center pointer-events-none transition ${isPinned ? 'text-amber-400' : 'text-slate-300'}`} aria-hidden>
                                                                    <Star className={`w-[18px] h-[18px] ${isPinned ? 'fill-current' : ''}`} />
                                                                </span>
                                                            </button>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    ))
                                )}
                                {drawerApps.length > 0 ? <div className='h-[40dvh] shrink-0' aria-hidden /> : null}
                            </div>

                            <div className='px-4 py-3 border-t border-slate-100 flex items-center justify-between bg-white shrink-0'>
                                <button type='button' onClick={scrollToTop} className='text-[12px] text-slate-500 hover:text-slate-800 inline-flex items-center gap-1'>
                                    <ChevronUp className='w-3.5 h-3.5' aria-hidden />
                                    {t('Topo')}
                                </button>
                            </div>
                        </div>

                        <div className='w-7 border-l border-gray-100 bg-gray-50/60 py-3 flex flex-col items-center justify-center gap-[1px] select-none shrink-0'>
                            {ALPHABET_INDEX.map((letter) => {
                                const hasApps = availableLetters.has(letter);
                                const isActive = activeLetter === letter;
                                return (
                                    <button
                                        key={letter}
                                        type='button'
                                        disabled={!hasApps}
                                        onClick={() => scrollToLetter(letter)}
                                        className={`w-5 h-[18px] rounded text-[10px] font-semibold leading-none transition ${isActive ? 'bg-slate-900 text-white' : hasApps ? 'text-slate-500 hover:text-indigo-600' : 'text-slate-300 cursor-default'}`}
                                    >
                                        {letter}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </DrawerContent>
            </Drawer>

            <Modal open={modalAppOpen} onOpenChange={setModalAppOpen}>
                <ModalContent className='h-auto'>{appActive && <AppModal app={appActive} />}</ModalContent>
            </Modal>
        </aside>
    );
};

export default NavigationAppsQuickAcessMenu;
